import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { NO_DATA } from "@/constants";
import { formatDateTime } from "@/lib/utils/formatters";
import type { AgreementFileDownloadsSummary } from "../../hooks/useAgreementFileDownloads";
import { AgreementFileDownloadsDialog } from "./AgreementFileDownloadsDialog";

interface AgreementFileDownloadsInfoProps {
  /** Nazwa pliku (tytuł okna historii) */
  filename: string;
  /** Podsumowanie pobrań tego pliku; brak = plik nie był pobierany ani podglądany */
  summary?: AgreementFileDownloadsSummary;
}

/**
 * Linia „Ostatnio pobrano / Ostatnio wyświetlono podgląd: data / użytkownik (rola) / łącznie N" pod plikiem umowy
 * (ASMMNT-83, etap 2). „łącznie N" otwiera okno „Historia pobrań". Bez zdarzeń pokazuje
 * „Nie pobrano"; gdy wszystkie zdarzenia pliku wypadły poza przyciętą listę szczegółów,
 * zostaje czas ostatniego dostępu z agregatu.
 */
export const AgreementFileDownloadsInfo = ({
  filename,
  summary,
}: AgreementFileDownloadsInfoProps) => {
  const { t } = useTranslation();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  const openHistory = useCallback(() => setIsHistoryOpen(true), []);
  const closeHistory = useCallback(() => setIsHistoryOpen(false), []);

  if (!summary) {
    return (
      <span className="text-xs text-gray-600">
        {t("agreements.fileDownloads.never")}
      </span>
    );
  }

  const { last } = summary;
  const date = formatDateTime(summary.lastDownloadedAt);
  const lastLine = last
    ? t(
        last.mode === "preview"
          ? "agreements.fileDownloads.lastPreview"
          : "agreements.fileDownloads.last",
        {
          date,
          // CONCAT_WS w widoku daje pusty tekst, gdy konto użytkownika zniknęło.
          name: last.downloaded_by_name || NO_DATA,
          role: last.downloaded_by_role_name || NO_DATA,
        },
      )
    : t("agreements.fileDownloads.lastTrimmed", { date });

  return (
    <span className="inline-flex flex-wrap items-center gap-x-1 text-xs text-gray-600">
      <span>{lastLine}</span>
      <span aria-hidden="true">/</span>
      <Button
        type="button"
        variant="link"
        className="h-auto p-0 text-xs font-normal underline"
        onClick={openHistory}
      >
        {t("agreements.fileDownloads.total", { total: summary.count })}
        {/* Kontekst pliku tylko dla czytników ekranu: widoczny tekst zostaje częścią nazwy dostępnej (WCAG 2.5.3). */}
        <span className="sr-only">
          {`, ${t("agreements.fileDownloads.historyFor", { filename })}`}
        </span>
      </Button>
      <AgreementFileDownloadsDialog
        open={isHistoryOpen}
        onClose={closeHistory}
        filename={filename}
        entries={summary.entries}
        total={summary.count}
      />
    </span>
  );
};
